// Display states never manufacture outcome counts or derive costs from a
// potentially incomplete conversion total.
import type { AdsDeliveryCoverage } from "@shared/ads-manager-delivery-evidence";
import { selectedPurchaseCount, type AdsObservedLead, type AdsSelectedPurchase } from "@shared/ads-manager";

export function effectivePurchaseMetrics<T extends ReportingMetrics>(metrics?: T): T | undefined {
  if (!metrics || metrics.purchaseResults != null) return metrics;
  const count = selectedPurchaseCount(metrics.selectedPurchase);
  if (count === null) return metrics;
  return { ...metrics, purchaseResults: count,
    costPerPurchase: count > 0 ? metrics.selectedPurchase!.costPerPurchase : null };
}

/** Presentation only: preserve action priority and the qualified fallback. */
export function purchaseOutcomeDisplay(metrics?: ReportingMetrics) {
  const count = effectivePurchaseMetrics(metrics)?.purchaseResults;
  if (count == null) return null;
  return {
    label: `${count.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${count === 1 ? "purchase" : "purchases"}`,
    basis: metrics?.purchaseResults == null
      ? "Meta-selected purchase results; default attribution. Separate from full-action purchase totals; cost uses the same retained spend population. Collection freshness is reported separately."
      : "Full-action purchase totals.",
  };
}

/** Never use row observations for summary metrics or recommendation ranking. */
export function observedLeadMetrics<T extends ReportingMetrics>(metrics?: T, eligible = false): T | undefined {
  if (!metrics?.observedLead) return metrics;
  // Preserve the established objective-scoped KPI when it exists. Observations
  // add row coverage; their absence must not erase a retained eligible result.
  if (eligible && metrics.leadResults != null && Number.isFinite(metrics.leadResults)
    && metrics.leadResults >= 0) return metrics;
  const observation = metrics.observedLead;
  const count = observation.coverage.state === "complete" && observation.count != null
    && Number.isFinite(observation.count) && observation.count >= 0 ? observation.count : null;
  const cost = count != null && count > 0 && observation.costPerLead != null
    && Number.isFinite(observation.costPerLead) && observation.costPerLead >= 0 ? observation.costPerLead : null;
  // Objective-scoped coverage does not describe this independent observation.
  // The observation's own coverage was checked above; do not let the original
  // unavailable KPI coverage hide verified incidental leads on Sales rows.
  return { ...metrics, leadResults: count, costPerLead: cost, leadCoverage: undefined };
}

export type ReportingMetrics = {
  selectedPurchase?: AdsSelectedPurchase;
  leadCoverage?: { state: "complete" | "partial" | "unavailable" };
  observedLead?: AdsObservedLead;
  deliveryCoverage?: AdsDeliveryCoverage;
  spend?: number | null;
  impressions?: number | null;
  clicks?: number | null;
  cpm?: number | null;
  ctr?: number | null;
  purchaseResults?: number | null;
  costPerPurchase?: number | null;
  leadResults?: number | null;
  costPerLead?: number | null;
};

/** Format supplied lead evidence only; row observations must be projected first. */
export function leadDisplayValues(metrics?: ReportingMetrics, currency?: string | null) {
  const count = metrics?.leadResults;
  const known = count != null && Number.isFinite(count) && count >= 0
    && (!metrics?.leadCoverage || metrics.leadCoverage.state === "complete");
  const cost = metrics?.costPerLead;
  const validCost = known && count > 0 && cost != null && Number.isFinite(cost) && cost >= 0;
  return {
    leads: known ? count.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "N/A",
    costPerLead: validCost && currency && /^[A-Z]{3}$/.test(currency)
      ? new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 2 }).format(cost)
      : "N/A",
  };
}

export const hasNoReportedDelivery = (metrics?: ReportingMetrics) =>
  metrics?.deliveryCoverage?.state === "complete" && metrics.deliveryCoverage.totalAds > 0
  && metrics.deliveryCoverage.knownAds === metrics.deliveryCoverage.totalAds
  && metrics.spend === 0 && metrics.impressions === 0 && metrics.clicks === 0;

export function reportingRate(
  metrics: ReportingMetrics | undefined,
  key: "cpm" | "ctr" | "costPerPurchase" | "costPerLead",
): { value: number | null; reason: string | null } {
  metrics = effectivePurchaseMetrics(metrics);
  if ((key === "cpm" || key === "ctr") && metrics?.impressions === 0) {
    const label = key === "cpm" ? "CPM" : "CTR";
    return { value: null, reason: metrics.deliveryCoverage?.state === "complete"
      ? `${label} does not apply: no impressions were reported for the selected dates.`
      : `Complete impression evidence was not returned for the selected dates. ${label} cannot be calculated.` };
  }
  if (key === "costPerPurchase" && metrics?.purchaseResults === 0) {
    return { value: null, reason: "Cost per purchase does not apply: the reported purchase count is zero." };
  }
  if (key === "costPerLead" && metrics?.leadResults === 0) {
    return { value: null, reason: "Cost per lead does not apply: the reported lead count is zero." };
  }
  const value = metrics?.[key];
  if (value != null && Number.isFinite(value) && value >= 0) return { value, reason: null };
  if (key === "costPerPurchase") {
    return { value: null, reason: metrics?.purchaseResults == null
      ? "A purchase count was not returned for the selected dates. Cost per purchase cannot be calculated."
      : "A purchase count was reported, but a matching cost per purchase was not returned." };
  }
  if (key === "costPerLead") {
    return { value: null, reason: "A complete matching lead count and lead cost were not returned for the selected dates." };
  }
  return { value: null, reason: `A matching ${key === "cpm" ? "CPM" : "CTR"} was not returned for the selected dates.` };
}
